import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

class WebSocketService {
  constructor() {
    this.client = null;
    this.messageSubscriptions = [];
    this.notificationSubscriptions = [];
    this.connectingPromise = null; // Promise aktualnego połączenia, aby uniknąć duplikacji
    this.lastConnectToken = null;
  }

  connect(token) {
    // Jeśli klient jest już połączony, zwróć natychmiast
    if (this.client?.connected === true) {
      console.log('✅ WebSocket already connected');
      return Promise.resolve();
    }

    // Jeśli klient już istnieje i jest aktywny (łączy się / będzie reconnectował),
    // to NIE twórz nowego. Wystarczy poczekać na połączenie.
    if (this.client?.active === true) {
      if (!this.connectingPromise) {
        this.connectingPromise = new Promise((resolve, reject) => {
          const startedAt = Date.now();
          const timer = setInterval(() => {
            if (this.client?.connected === true) {
              clearInterval(timer);
              this.connectingPromise = null;
              resolve();
              return;
            }
            if (Date.now() - startedAt > 15000) {
              clearInterval(timer);
              this.connectingPromise = null;
              reject(new Error('Connection timeout - waiting for active client to connect'));
            }
          }, 100);
        });
      }
      return this.connectingPromise;
    }

    // Jeśli połączenie jest w trakcie, poczekaj na nie
    if (this.connectingPromise) {
      console.log('⏳ WebSocket connection already in progress, waiting...');
      return this.connectingPromise;
    }

    // Jeśli mamy starego klienta, ale nie jest aktywny (np. po błędzie) – wyczyść go,
    // żeby nie trzymać śmieci w pamięci. Bez tworzenia pętli reconnect.
    if (this.client && this.client.active !== true) {
      this.client = null;
    }

    // Resetuj flagę celowego zamknięcia przed połączeniem
    this.intentionalDisconnect = false;
    this.lastConnectToken = token;

    // Zapisz Promise połączenia, aby uniknąć wielokrotnych prób
    this.connectingPromise = new Promise((resolve, reject) => {
      // Backend wymaga SockJS (z .withSockJS()), więc MUSIMY używać SockJS
      // SockJS używa http/https, nie wss/ws - automatycznie przełączy się na WebSocket
      const protocol = window.location.protocol;
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/ws`;

      console.log('Connecting to WebSocket via SockJS:', wsUrl);

      let connectionTimeout;
      let resolved = false;

      this.client = new Client({
        webSocketFactory: () => {
          // Wyłączamy WebSocket całkowicie - używamy tylko transportów HTTP
          // To eliminuje problemy z WebSocket Secure (wss://) w Firefoxie
          // SockJS automatycznie wykluczy WebSocket jeśli nie jest w liście transportów
          return new SockJS(wsUrl, null, {
            transports: ['xhr-streaming', 'xhr-polling']
          });
        },
        connectHeaders: {
          Authorization: `Bearer ${token}`
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        // Włącz automatyczne ponowne połączenie
        connectionTimeout: 10000,
        debug: (str) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('STOMP:', str);
          }
        },
        onConnect: () => {
          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
          }
          if (!resolved) {
            resolved = true;
            console.log('✅ WebSocket connected successfully');
            // Wyczyść Promise połączenia
            this.connectingPromise = null;
            // Natychmiast subskrybuj - onConnect jest wywoływany gdy STOMP jest połączony
            this.resubscribeAll();
            resolve();
          }
        },
        onWebSocketClose: (event) => {
          // Wyczyść Promise połączenia przy zamknięciu
          this.connectingPromise = null;
          
          // Ignoruj błędy podczas celowego rozłączenia
          if (this.intentionalDisconnect) {
            console.debug('WebSocket closed during intentional disconnect');
            return;
          }
          
          // Loguj tylko ważne błędy - pomiń kod 1002 (protocol error) jeśli backend nie jest dostępny
          // Kod 1002 często występuje gdy backend nie odpowiada lub nginx nie przekazuje poprawnie
          if (event.code === 1002) {
            // Loguj tylko raz na 10 sekund, aby nie zaśmiecać konsoli
            const now = Date.now();
            if (!this.lastErrorLog || now - this.lastErrorLog > 10000) {
              this.lastErrorLog = now;
              console.warn('⚠️ WebSocket connection failed (backend may be unavailable). Will retry automatically.');
            }
          } else if (event.code !== 1000) {
            console.log('⚠️ WebSocket closed:', event);
          } else {
            console.debug('WebSocket closed normally (code 1000)');
          }
          
          // Jeśli połączenie zostało zamknięte przed zakończeniem Promise, odrzuć je
          if (!resolved) {
            resolved = true;
            if (connectionTimeout) {
              clearTimeout(connectionTimeout);
            }
            // Ignoruj normalne zamknięcia (code 1000) - mogą być częścią reconnect
            // Dla kodu 1002, odrzuć Promise ale STOMP.js automatycznie spróbuje ponownie
            if (event.code !== 1000) {
              reject(new Error(`WebSocket closed: ${event.reason || 'Unknown reason'}`));
            }
          }
          // STOMP.js ma wbudowany reconnect, nie trzeba robić własnego
        },
        onStompError: (frame) => {
          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
          }
          if (!resolved) {
            resolved = true;
            // Wyczyść Promise połączenia
            this.connectingPromise = null;
            console.error('❌ STOMP error:', frame);
            const errorMessage = frame.headers?.['message'] || frame.body || 'Connection failed';
            reject(new Error(errorMessage));
          }
        },
        onWebSocketError: (event) => {
          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
          }
          if (!resolved) {
            resolved = true;
            // Wyczyść Promise połączenia
            this.connectingPromise = null;
            // Ignoruj błędy podczas celowego rozłączenia lub cleanup
            if (this.intentionalDisconnect) {
              console.debug('WebSocket error during intentional disconnect, ignoring');
              return;
            }
            // Loguj tylko jeśli to nie jest błąd podczas cleanup starego klienta
            if (this.client) {
              // Ogranicz logowanie błędów - loguj tylko raz na 10 sekund
              const now = Date.now();
              if (!this.lastErrorLog || now - this.lastErrorLog > 10000) {
                this.lastErrorLog = now;
                console.warn('⚠️ WebSocket connection error (backend may be unavailable). Will retry automatically.');
              }
              reject(new Error('WebSocket connection error'));
            } else {
              // Klient został już usunięty, ignoruj błąd
              console.debug('WebSocket error after client cleanup, ignoring');
            }
          }
        },
        onDisconnect: () => {
          // Resetuj reconnect attempts jeśli było to zamknięcie celowe
          if (this.intentionalDisconnect) {
            // Możemy zresetować stan jeśli potrzeba
          }
        }
      });

      // Aktywuj klienta - to rozpocznie połączenie
      console.log('Activating STOMP client...');
      this.client.activate();
      
      console.log('STOMP client activation initiated');
      
      // Timeout dla połączenia - jeśli nie połączy się w ciągu 15 sekund, odrzuć Promise
      connectionTimeout = setTimeout(() => {
        if (!resolved && !this.client?.connected) {
          resolved = true;
          // Wyczyść Promise połączenia
          this.connectingPromise = null;
          console.error('⏱️ WebSocket connection timeout after 15 seconds');
          reject(new Error('Connection timeout - WebSocket failed to connect within 15 seconds'));
        }
      }, 15000);
    });

    return this.connectingPromise;
  }

  disconnect() {
    this.intentionalDisconnect = true;
    if (this.client) {
      // Usuń wszystkie subskrypcje
      this.messageSubscriptions = [];
      this.notificationSubscriptions = [];
      
      // Bezpiecznie dezaktywuj tylko jeśli klient jest aktywny i połączony
      if (this.client.active && this.client.connected) {
        try {
          this.client.deactivate();
        } catch (e) {
          console.warn('Error during disconnect (non-critical):', e.message);
        }
      }
      
      // Zawsze usuń referencję
      this.client = null;
    }
  }

  resubscribeAll() {
    if (!this.client?.connected) {
      return;
    }

    // Jeśli już mamy aktywną subskrypcję dla wiadomości, nie tworzmy nowej
    // Tylko dodajmy brakujące callbacki
    const hasActiveMessageSubscription = this.messageSubscriptions.some(sub => sub.subscription);
    
    if (!hasActiveMessageSubscription && this.messageSubscriptions.length > 0) {
      try {
        // Utwórz jedną subskrypcję, która wywołuje wszystkie callbacki
        const subscription = this.client.subscribe('/user/queue/messages', (message) => {
          try {
            const data = JSON.parse(message.body);
            // Wywołaj wszystkie callbacki
            this.messageSubscriptions.forEach(sub => {
              if (sub.callback) {
                try {
                  sub.callback(data);
                } catch (error) {
                  console.error('Error in message callback:', error);
                }
              }
            });
          } catch (error) {
            console.error('Error parsing message:', error);
          }
        });
        
        if (subscription) {
          // Oznacz wszystkie callbacki jako mające tę samą subskrypcję
          this.messageSubscriptions = this.messageSubscriptions.map(sub => ({
            ...sub,
            subscription
          }));
        }
      } catch (error) {
        console.error('Error subscribing to messages:', error, error.stack);
      }
    }
    
    // Jeśli już mamy aktywną subskrypcję dla powiadomień, nie tworzmy nowej
    const hasActiveNotificationSubscription = this.notificationSubscriptions.some(sub => sub.subscription);
    
    if (!hasActiveNotificationSubscription && this.notificationSubscriptions.length > 0) {
      try {
        // Utwórz jedną subskrypcję, która wywołuje wszystkie callbacki
        const subscription = this.client.subscribe('/user/queue/notifications', (message) => {
          try {
            const data = JSON.parse(message.body);
            // Wywołaj wszystkie callbacki
            this.notificationSubscriptions.forEach(sub => {
              if (sub.callback) {
                try {
                  sub.callback(data);
                } catch (error) {
                  console.error('Error in notification callback:', error);
                }
              }
            });
          } catch (error) {
            console.error('Error parsing notification:', error);
          }
        });
        
        if (subscription) {
          // Oznacz wszystkie callbacki jako mające tę samą subskrypcję
          this.notificationSubscriptions = this.notificationSubscriptions.map(sub => ({
            ...sub,
            subscription
          }));
        }
      } catch (error) {
        console.error('Error subscribing to notifications:', error, error.stack);
      }
    }
  }

  subscribeToMessages(callback) {
    // Sprawdź czy callback już istnieje
    const existingIndex = this.messageSubscriptions.findIndex(s => s.callback === callback);
    
    if (existingIndex >= 0) {
      // Callback już istnieje - jeśli jest połączony, subskrypcja już działa
      return;
    }
    
    // Dodaj callback do listy
    this.messageSubscriptions.push({ callback });
    
    // Jeśli jesteśmy połączeni, utwórz lub zaktualizuj subskrypcję
    if (this.client?.connected === true) {
      this.resubscribeAll();
    }
  }

  subscribeToNotifications(callback) {
    // Sprawdź czy callback już istnieje
    const existingIndex = this.notificationSubscriptions.findIndex(s => s.callback === callback);
    
    if (existingIndex >= 0) {
      // Callback już istnieje - jeśli jest połączony, subskrypcja już działa
      return;
    }
    
    // Dodaj callback do listy
    this.notificationSubscriptions.push({ callback });
    
    // Jeśli jesteśmy połączeni, utwórz lub zaktualizuj subskrypcję
    if (this.client?.connected === true) {
      this.resubscribeAll();
    }
  }

  sendMessage(recipientId, content) {
    if (!this.client?.connected) {
      console.error('WebSocket not connected. State:', { 
        connected: this.client?.connected, 
        hasClient: !!this.client
      });
      throw new Error('WebSocket not connected');
    }

    const messagePayload = {
      recipientId,
      content
    };
    
    try {
      this.client.publish({
        destination: '/app/chat.send',
        body: JSON.stringify(messagePayload)
      });
    } catch (error) {
      console.error('Error publishing message:', error);
      throw error;
    }
  }

  unsubscribeFromMessages(callback) {
    const index = this.messageSubscriptions.findIndex(s => s.callback === callback);
    if (index >= 0) {
      this.messageSubscriptions.splice(index, 1);
    }
  }

  unsubscribeFromNotifications(callback) {
    const index = this.notificationSubscriptions.findIndex(s => s.callback === callback);
    if (index >= 0) {
      this.notificationSubscriptions.splice(index, 1);
    }
  }

  isConnected() {
    return this.client?.connected === true;
  }
}

// Singleton instance
const websocketService = new WebSocketService();
export default websocketService;
